import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

/**
 * Where the API token is kept between launches. **Never `localStorage`**
 * (InGo mobile plan M1).
 *
 * - On the phone: `capacitor-secure-storage-plugin`, which encrypts the value
 *   with a key held in the Android Keystore. Chosen 29 Sep 2026: MIT, Capacitor
 *   8, no iOS dependency (`@aparajita/capacitor-secure-storage` pulls in
 *   `@capacitor/ios`, and this app has no iOS project).
 * - On the website: `sessionStorage`, so a token ends with its tab. The plugin
 *   is never called on the web: its web fallback is base64 in `localStorage`.
 *
 * The legacy sessions (`lib/customerSession.js`, `lib/driverSession.js`) are
 * not this: they stay until each sign-in moves to the API.
 */
export const TOKEN_KEY = 'ingo_api_token';

/** The phone's store. A missing key is a rejection in the plugin; here it is `null`. */
export function secureStorageBackend(plugin = SecureStoragePlugin) {
  return {
    async get(key) {
      try {
        const { value } = await plugin.get({ key });
        return typeof value === 'string' && value !== '' ? value : null;
      } catch {
        return null;
      }
    },
    async set(key, value) {
      await plugin.set({ key, value });
    },
    async remove(key) {
      try {
        await plugin.remove({ key });
      } catch {
        // Nothing stored, nothing to remove.
      }
    },
  };
}

/** The website's store. Private browsing can refuse storage; then the token lives for this page only. */
export function sessionStorageBackend(storage = () => window.sessionStorage) {
  return {
    async get(key) {
      try {
        return storage().getItem(key);
      } catch {
        return null;
      }
    },
    async set(key, value) {
      try {
        storage().setItem(key, value);
      } catch {
        // Held in memory by the store instead.
      }
    },
    async remove(key) {
      try {
        storage().removeItem(key);
      } catch {
        // Nothing stored, nothing to remove.
      }
    },
  };
}

/**
 * A token store over one backend, read once and then held in memory, so a
 * request does not wait on the Keystore every time.
 *
 * `write` rejects when the phone could not keep the token (a Keystore fault):
 * the token still works until the app closes, and the caller says so.
 */
export function createTokenStore(backend) {
  let loaded = null;
  let token = null;

  const load = () => {
    if (loaded === null) {
      loaded = backend.get(TOKEN_KEY).then((value) => {
        token = value;
        return value;
      });
    }

    return loaded;
  };

  return {
    async read() {
      await load();
      return token;
    },
    async write(value) {
      token = value;
      loaded = Promise.resolve(value);
      await backend.set(TOKEN_KEY, value);
    },
    async clear() {
      token = null;
      loaded = Promise.resolve(null);
      await backend.remove(TOKEN_KEY);
    },
  };
}

export const tokenStore = createTokenStore(
  Capacitor.isNativePlatform() ? secureStorageBackend() : sessionStorageBackend(),
);
